import { useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { createRestriction } from "./api";
import { UPDATE_MSG } from "../../constant";
import { useToastrContext } from "../../components/Provider/ToastrProvider";
import PermissionGuard from "../../components/common/PermissionGuard";

const BulkRestriction = ({ hotelDetails, dateRange, contractType }) => {
  const [restrictions, setRestrictions] = useState({
    blockInventory: false,
    unblockInventory: false,
    closeToArrival: false,
    inactivateCTA: false,
    closeToDeparture: false,
    inactivateCTD: false,
    minLengthOfStay: null,
    cutoff: "1",
    cutoffType: "days",
    cutoffValue: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { errorResponseHandler } = useToastrContext();
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;

    const exclusivePairs = {
      blockInventory: "unblockInventory",
      unblockInventory: "blockInventory",
      closeToArrival: "inactivateCTA",
      inactivateCTA: "closeToArrival",
      closeToDeparture: "inactivateCTD",
      inactivateCTD: "closeToDeparture",
    };

    setRestrictions((prevState) => {
      let newState = { ...prevState, [name]: checked };

      if (checked && exclusivePairs[name]) {
        newState[exclusivePairs[name]] = false;
      }

      return newState;
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRestrictions({ ...restrictions, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        ...dateRange,
        contractType,
        hotelId: hotelDetails.id,
        restrictions: restrictions,
      }

      const response = await createRestriction(payload);
      if ([200, 201]?.includes(response?.status)) {
        errorResponseHandler(UPDATE_MSG, 'success');
      }
    } catch (error) {
      errorResponseHandler(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="form-body">
        <div className="p-4">
          <h5>Update Restrictions for Entire Hotel</h5>
          <p className="text-muted">
            Please note, any restrictions you apply from here will be applied on
            all rooms and rates.
          </p>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col>
                <Form.Check
                  type="checkbox"
                  label="Block Inventory"
                  name="blockInventory"
                  checked={restrictions.blockInventory}
                  onChange={handleCheckboxChange}
                />
                <Form.Check
                  type="checkbox"
                  label="Unblock Inventory"
                  name="unblockInventory"
                  checked={restrictions.unblockInventory}
                  onChange={handleCheckboxChange}
                />
              </Col>
              <Col>
                <Form.Check
                  type="checkbox"
                  label="Close to Arrival (CTA)"
                  name="closeToArrival"
                  checked={restrictions.closeToArrival}
                  onChange={handleCheckboxChange}
                />
                <Form.Check
                  type="checkbox"
                  label="Inactivate CTA"
                  name="inactivateCTA"
                  checked={restrictions.inactivateCTA}
                  onChange={handleCheckboxChange}
                />
              </Col>
              <Col>
                <Form.Check
                  type="checkbox"
                  label="Close to Departure (CTD)"
                  name="closeToDeparture"
                  checked={restrictions.closeToDeparture}
                  onChange={handleCheckboxChange}
                />
                <Form.Check
                  type="checkbox"
                  label="Inactivate CTD"
                  name="inactivateCTD"
                  checked={restrictions.inactivateCTD}
                  onChange={handleCheckboxChange}
                />
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group controlId="minLengthOfStay">
                  <Form.Label>Set Minimum Length of Stay</Form.Label>
                  <Form.Control
                    type="number"
                    name="minLengthOfStay"
                    value={restrictions.minLengthOfStay}
                    onChange={handleInputChange}
                    placeholder="Enter days"
                    className="uniform-height"
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="cutoff">
                  <Form.Label>Set Cutoff</Form.Label>
                  <Form.Select
                    name="cutoff"
                    value={restrictions.cutoff}
                    onChange={handleInputChange}
                    className="uniform-height"
                  >
                    <option value="1">At Midnight</option>
                    <option value="2">Before Midnight</option>
                    <option value="3">After Midnight</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              {restrictions.cutoff === "2" && (
                <>
                  <Col>
                    <Form.Label>Cutoff Type</Form.Label>
                    <Form.Select
                      name="cutoffType"
                      value={restrictions.cutoffType}
                      onChange={handleInputChange}
                      className="uniform-height"
                    >
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                    </Form.Select>
                  </Col>
                  <Col>
                    <Form.Label>Cutoff Value</Form.Label>
                    <Form.Control
                      type="number"
                      name="cutoffValue"
                      value={restrictions.cutoffValue}
                      onChange={handleInputChange}
                      placeholder={`Enter ${restrictions.cutoffType}`}
                      className="uniform-height"
                    />
                  </Col>
                </>
              )}
              {restrictions.cutoff === "3" && (
                <Col>
                  <Form.Label>Cutoff Hours (up to 6)</Form.Label>
                  <Form.Select
                    name="cutoffValue"
                    value={restrictions.cutoffValue}
                    onChange={handleInputChange}
                    className="uniform-height"
                  >
                    <option value="">Select</option>
                    <option value="1">1 hours</option>
                    <option value="2">2 hours</option>
                    <option value="3">3 hours</option>
                    <option value="4">4 hours</option>
                    <option value="5">5 hours</option>
                    <option value="6">6 hours</option>
                  </Form.Select>
                </Col>
              )}
            </Row>
            <PermissionGuard permissions={["rate:edit"]}>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Processing...
                  </>
                ) : (
                  "Apply Restrictions"
                )}
              </Button>
            </PermissionGuard>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default BulkRestriction;
